import { useState } from "react";
import { auth, functions } from "../firebase";
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { setStoredLoginMode } from "../utils/loginMode";
import { userFacingError } from "../utils/userFacingErrors";

const STAFF_ROLES = new Set([
  "manager",
  "assistant",
  "accounting",
  "maintenance",
  "viewer",
]);

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const navigate = useNavigate();

  const showRecoveryConfirmation = () => {
    setRecoveryMessage("If an account exists for this email, you will receive a password reset link. Check your inbox and spam folder, then return here to log in with your new password.");
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    setRecoveryMessage("");
    try {
      await sendPasswordResetEmail(auth, email.trim(), {
        url: `${window.location.origin}/manager-login`,
      });
      showRecoveryConfirmation();
    } catch (err) {
      // Do not expose whether an email has an account, including on projects
      // without Firebase email enumeration protection.
      if (err?.code === "auth/user-not-found") {
        showRecoveryConfirmation();
      } else {
        setError(userFacingError(err, "Unable to send a password reset email. Check the email address and try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);

      // Ensure user profile exists + role is correct
      try {
        const fn = httpsCallable(functions, "syncUserProfile");
        const res = await fn({});
        const role = String(res?.data?.role || "").trim();
        if (!STAFF_ROLES.has(role)) {
          await signOut(auth);
          setError("Not authorized for property staff access");
          return;
        }
      } catch (e) {
        console.error(e);
        await signOut(auth);
        setError("Login failed (profile sync).");
        return;
      }

      // 🔥 GO DIRECTLY TO PROPERTY SELECTOR AFTER LOGIN
      const nextMode = setStoredLoginMode("staff");
      if (typeof onLogin === "function") onLogin(nextMode);
      navigate("/select-property");

    } catch (err) {
      console.error(err);
      setError(userFacingError(err, "Login failed. Check your email and password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-20 p-6 border rounded-xl shadow">
      <h2 className="text-xl font-bold mb-4">{recoveryMode ? "Reset your password" : "Property Manager Login"}</h2>
      {recoveryMode && <p className="mb-4">Enter your account email to receive a password reset link.</p>}
      <form onSubmit={recoveryMode ? handlePasswordReset : handleSubmit} className="space-y-4">
        <input
          aria-label="Email address"
          name="email"
          autoComplete="username"
          type="email"
          placeholder="Email"
          className="w-full p-2 border rounded"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setRecoveryMessage(""); setError(""); }}
          disabled={submitting}
          required
        />
        {!recoveryMode && <input
          aria-label="Password"
          name="password"
          autoComplete="current-password"
          type="password"
          placeholder="Password"
          className="w-full p-2 border rounded"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
          required
        />}
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {recoveryMessage && <p role="status">{recoveryMessage}</p>}
        <button type="submit" disabled={submitting} className="w-full bg-blue-600 text-white py-2 rounded">
          {recoveryMode ? (submitting ? "Sending reset email..." : "Send reset email") : (submitting ? "Signing in..." : "Log In")}
        </button>
      </form>
      <button
        type="button"
        disabled={submitting}
        className="mt-4 text-blue-700 underline"
        onClick={() => {
          setRecoveryMode(!recoveryMode);
          setPassword("");
          setError("");
          setRecoveryMessage("");
        }}
      >
        {recoveryMode ? "Back to login" : "Reset password"}
      </button>
    </div>
  );
}
